// src/pages/DHLSendSign.jsx
import React, { useRef, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, TextField, Button, Typography } from '@mui/material';
import easyLogo from '../assets/images/easy_logo.png';
import dhlLogo from '../assets/images/dhllogo.png';
import Services from 'src/services/Services';
import { toast } from 'react-toastify';

const DHLSendSign = () => {
    const { barcode } = useParams();
    const [inputValue, setInputValue] = useState(barcode || '');
    const canvasWrapperRef = useRef(null);
    const canvasRef = useRef(null);
    const signatureSnapshotRef = useRef(null);
    const hasSignatureRef = useRef(false);

    // Canvas’ın CSS boyutuna göre çözünürlüğünü ayarlamak için state
    const [canvasSize, setCanvasSize] = useState({ width: 500, height: 300 });
    const [hasSignature, setHasSignature] = useState(false);

    // URL’den barkod geldiğinde input’u doldur
    useEffect(() => {
        if (barcode) {
            setInputValue(barcode);
        }
    }, [barcode]);

    // Canvas boyutunu wrapper'ın genişliğine göre güncelle
    useEffect(() => {
        const updateCanvasSize = () => {
            if (canvasWrapperRef.current) {
                const existingCanvas = canvasRef.current;
                if (existingCanvas && hasSignatureRef.current) {
                    signatureSnapshotRef.current = existingCanvas.toDataURL('image/png');
                }

                const wrapperWidth = canvasWrapperRef.current.getBoundingClientRect().width;
                const newWidth = Math.min(wrapperWidth, 500);
                const aspectRatio = 166 / 519; // yükseklik / en
                const newHeight = newWidth * aspectRatio;
                setCanvasSize({ width: newWidth, height: newHeight });
            }
        };

        updateCanvasSize();
        window.addEventListener('resize', updateCanvasSize);
        return () => window.removeEventListener('resize', updateCanvasSize);
    }, []);

    // Çizim eventlerini ekle
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        if (signatureSnapshotRef.current) {
            const savedImage = new Image();
            savedImage.onload = () => {
                ctx.drawImage(savedImage, 0, 0, canvas.width, canvas.height);
            };
            savedImage.src = signatureSnapshotRef.current;
        }

        let isDrawing = false;
        let lastPos = { x: 0, y: 0 };

        const getCanvasPos = (e) => {
            const rect = canvas.getBoundingClientRect();
            return {
                x: (e.clientX - rect.left) * (canvas.width / rect.width),
                y: (e.clientY - rect.top) * (canvas.height / rect.height),
            };
        };

        const handlePointerDown = (e) => {
            e.preventDefault();
            isDrawing = true;
            lastPos = getCanvasPos(e);
            canvas.setPointerCapture?.(e.pointerId);
        };

        const handlePointerMove = (e) => {
            if (!isDrawing) return;
            e.preventDefault();
            const { x, y } = getCanvasPos(e);
            ctx.beginPath();
            ctx.moveTo(lastPos.x, lastPos.y);
            ctx.lineTo(x, y);
            ctx.stroke();
            lastPos = { x, y };
            if (!hasSignatureRef.current) {
                hasSignatureRef.current = true;
                setHasSignature(true);
            }
        };

        const handlePointerUp = (e) => {
            isDrawing = false;
            if (hasSignatureRef.current) {
                signatureSnapshotRef.current = canvas.toDataURL('image/png');
            }
            canvas.releasePointerCapture?.(e.pointerId);
        };

        canvas.addEventListener('pointerdown', handlePointerDown);
        canvas.addEventListener('pointermove', handlePointerMove);
        canvas.addEventListener('pointerup', handlePointerUp);
        canvas.addEventListener('pointercancel', handlePointerUp);

        return () => {
            canvas.removeEventListener('pointerdown', handlePointerDown);
            canvas.removeEventListener('pointermove', handlePointerMove);
            canvas.removeEventListener('pointerup', handlePointerUp);
            canvas.removeEventListener('pointercancel', handlePointerUp);
        };
    }, [canvasSize]);

    const clearCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        signatureSnapshotRef.current = null;
        hasSignatureRef.current = false;
        setHasSignature(false);
    };

    // --- Burada istediğin fonksiyon ---
    const sendDHLSign = () => {
        const canvas = canvasRef.current;
        if (!canvas) {
            toast.error('İmza alanı bulunamadı.');
            return;
        }

        if (!inputValue) {
            toast.error('Lütfen önce barkodu girin.');
            return;
        }

        if (!hasSignatureRef.current) {
            toast.error('Lütfen önce imzanızı atın.');
            return;
        }

        // Canvas'tan PNG formatında base64 string al
        const dataURL = canvas.toDataURL('image/png');
        // "data:image/png;base64,..." kısmını kırpıp sadece base64 veriyi alıyoruz
        const base64Signature = dataURL.replace(/^data:image\/png;base64,/, '');

        // Service çağrısı: barkod ve base64 imza verisi
        Services.DHLSendSign(inputValue, base64Signature)
            .then((res) => {
                if (res.data.status) {
                    toast.success('İmza başarıyla gönderildi.');
                    clearCanvas();
                } else {
                    toast.error(`Gönderme hatası: ${res.data.message || 'Bilinmeyen hata'}`);
                    console.error('Hata detayı:', res.data);
                }
            })
            .catch((err) => {
                toast.error('İmza gönderilirken bir hata oluştu.');
                console.error('Servis çağrısı hatası:', err);
            });
    };
    // --- Fonksiyon sonu ---

    return (
        <Box
            sx={{
                minHeight: '100vh',
                backgroundColor: '#FFCC00',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                py: 4,
            }}
        >
            {/* Üstte logolar */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mb: 2,
                    width: '100%',
                }}
            >
                <Box
                    component="img"
                    src={easyLogo}
                    alt="Easy Logo"
                    sx={{
                        width: { xs: 80, sm: 120 },
                        height: { xs: 40, sm: 60 },
                        objectFit: 'contain',
                        mr: 1,
                    }}
                />
                <Box
                    component="img"
                    src={dhlLogo}
                    alt="DHL Logo"
                    sx={{
                        width: { xs: 80, sm: 120 },
                        height: { xs: 40, sm: 60 },
                        objectFit: 'contain',
                        ml: 1,
                    }}
                />
            </Box>

            {/* Barkod input */}
            <TextField
                label="Barkod"
                variant="outlined"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                disabled={!!barcode}
                sx={{
                    mb: 2,
                    width: '90%',
                    maxWidth: 400,
                    backgroundColor: '#FFFFFF',
                }}
            />

            {/* İmza başlığı */}
            <Typography variant="h6" sx={{ mb: 1 }}>
                İmza:
            </Typography>

            {/* Canvas wrapper: genişlik %90 viewport, max 500px */}
            <Box
                ref={canvasWrapperRef}
                sx={{
                    width: '90%',
                    maxWidth: 500,
                    backgroundColor: '#FFFFFF',
                    border: '2px solid #000',
                    cursor: 'crosshair',
                }}
            >
                <canvas
                    ref={canvasRef}
                    width={canvasSize.width}
                    height={canvasSize.height}
                    style={{
                        display: 'block',
                        width: '100%',
                        height: 'auto',
                        touchAction: 'none',
                    }}
                />
            </Box>

            {/* Butonlar */}
            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Button
                    variant="outlined"
                    onClick={clearCanvas}
                    sx={{ flex: '1 1 150px', maxWidth: 150, backgroundColor: '#fff' }}
                >
                    Temizle
                </Button>
                <Button
                    variant="outlined"
                    onClick={sendDHLSign}
                    disabled={!hasSignature}
                    sx={{ flex: '1 1 150px', maxWidth: 150, backgroundColor: '#fff' }}
                >
                    Teslim aldım. Onaylıyorum.
                </Button>
            </Box>
        </Box>
    );
};

export default DHLSendSign;
